import React, { useEffect, useState } from 'react';
import {
  EVENTO_DE_ESCRITA_NA_DEMO, EVENTO_DE_LANCAMENTO_NA_DEMO, SAIDA_DA_DEMO, MotivoDoAviso,
} from './modo';
import './FaixaDaDemo.css';

/**
 * A faixa fixa da demonstração, e o aviso de que nada é gravado.
 *
 * ── A faixa ──────────────────────────────────────────────────────────────
 *
 * Fica em toda tela, o tempo todo. É o que impede alguém de achar que aqueles
 * números são dele — ou que o app já sabe algo sobre ele. Uma demonstração que
 * se parece demais com a conta de verdade é uma mentira por omissão.
 *
 * ── O aviso ──────────────────────────────────────────────────────────────
 *
 * Aparece quando a pessoa tenta gravar alguma coisa. A camada de rede recusa a
 * escrita e dispara um evento (`services/api.ts`); aqui ele vira uma caixa
 * curta com o convite ao cadastro.
 *
 * É o momento certo de pedir: ela acabou de tentar USAR. E aparece na primeira
 * tentativa — descobrir que nada grava depois de preencher um formulário
 * inteiro é o jeito de a demonstração parecer quebrada.
 *
 * O lançamento é a exceção (28/10): o primeiro passa, e a faixa troca o
 * detalhe para dizer onde ele apareceu e que some ao recarregar. Sem caixa:
 * ela acabou de ver dar certo, e uma janela por cima esconderia a projeção
 * que mudou. O SEGUNDO abre o aviso, já dizendo que o de teste foi usado.
 *
 * Os links de cadastro vão para `/`, fora da demonstração: o modo é decidido
 * pelo endereço, então sair dele é só sair de `/demo`.
 */
const FaixaDaDemo: React.FC = () => {
  const [aviso, setAviso] = useState<MotivoDoAviso | null>(null);
  const [lancou, setLancou] = useState(false);

  useEffect(() => {
    const abrir = (e: Event) => setAviso((e as CustomEvent).detail === 'outro-lancamento' ? 'outro-lancamento' : 'escrita');
    const anotar = () => setLancou(true);
    window.addEventListener(EVENTO_DE_ESCRITA_NA_DEMO, abrir);
    window.addEventListener(EVENTO_DE_LANCAMENTO_NA_DEMO, anotar);
    return () => {
      window.removeEventListener(EVENTO_DE_ESCRITA_NA_DEMO, abrir);
      window.removeEventListener(EVENTO_DE_LANCAMENTO_NA_DEMO, anotar);
    };
  }, []);

  const verProjecao = (e: React.MouseEvent) => {
    const secao = document.getElementById('sec-projections');
    if (!secao) return;
    e.preventDefault();
    secao.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!aviso) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAviso(null); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aviso]);

  return (
    <>
      <div className="demo-faixa" role="status">
        <span className="demo-faixa-texto">
          <strong>Demonstração</strong>
          {lancou ? (
            <span className="demo-faixa-detalhe demo-faixa-lancou">
              {' · seu lançamento entrou na '}
              <a className="demo-faixa-ver" href="#sec-projections" onClick={verProjecao}>projeção</a>
              {' e some ao recarregar'}
            </span>
          ) : (
            <span className="demo-faixa-detalhe"> · dados fictícios, nada é gravado · faça um lançamento de teste</span>
          )}
        </span>
        <span className="demo-faixa-acoes">
          <a className="demo-faixa-sair" href={SAIDA_DA_DEMO}>Sair</a>
          <a className="demo-faixa-criar" href="/">Criar minha conta</a>
        </span>
      </div>

      {aviso && (
        <div className="demo-aviso-fundo" onClick={() => setAviso(null)} role="presentation">
          <div
            className="demo-aviso"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-aviso-titulo"
            onClick={e => e.stopPropagation()}
          >
            {aviso === 'outro-lancamento' ? (
              <>
                <h2 id="demo-aviso-titulo">O lançamento de teste já foi</h2>
                <p>
                  Você viu a projeção mudar com ele. Crie a sua conta para lançar
                  quantos quiser, com os seus números, e ver os seus próximos meses.
                </p>
              </>
            ) : (
              <>
                <h2 id="demo-aviso-titulo">Na demonstração nada é gravado</h2>
                <p>
                  Os números aqui são de uma pessoa inventada. Crie a sua conta para
                  lançar de verdade e ver os seus próximos meses.
                </p>
              </>
            )}
            <a className="demo-aviso-criar" href="/">Criar conta grátis</a>
            <button type="button" className="demo-aviso-continuar" onClick={() => setAviso(null)}>
              Continuar olhando
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FaixaDaDemo;
